import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useSelector } from "react-redux";

const AssetReportMap = ({
  mapHeight = "500px",
  statusFilter,
  divisionFilter,
  subDivisionFilter,
  DUMMY_ASSETS,
  statusColors,
}) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});

  const themeMode = useSelector((state) => state.theme.mode);
  const borderThem = themeMode ? "border_them_2" : "border-primary";

  useEffect(() => {
    if (mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      attributionControl: false,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
          },
        },
        layers: [{ id: "osm-layer", type: "raster", source: "osm" }],
      },
      center: [74.857, 32.7266],
      zoom: 11,
    });

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), "top-right");

    return () => map.remove();
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    Object.values(markersRef.current).forEach((marker) => marker.remove());
    markersRef.current = {};

    const filteredData = DUMMY_ASSETS.filter((asset) => {
      const statusMatch =
        statusFilter === "All" || asset.status === statusFilter;
      const divisionMatch =
        divisionFilter === "All" || asset.division === divisionFilter;
      const subDivisionMatch =
        subDivisionFilter === "All" || asset.subDivision === subDivisionFilter;

      return statusMatch && divisionMatch && subDivisionMatch;
    });

    filteredData.forEach((asset) => {
      const el = document.createElement("div");
      el.style.width = "20px";
      el.style.height = "20px";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.style.cursor = "pointer";

      const circle = document.createElement("div");
      circle.style.width = "10px";
      circle.style.height = "10px";
      circle.style.backgroundColor =
        statusColors[asset.status] || statusColors.Default;
      circle.style.borderRadius = "50%";
      circle.style.border = "1px solid white";
      circle.style.boxShadow = "0 0 8px rgba(0,0,0,0.3)";
      circle.style.transition = "transform 0.2s ease-in-out";

      el.appendChild(circle);

      el.onmouseover = () => {
        circle.style.transform = "scale(1.5)";
        el.style.zIndex = "100";
      };
      el.onmouseout = () => {
        circle.style.transform = "scale(1)";
        el.style.zIndex = "1";
      };

      const popupContent = `
        <div style="font-family: sans-serif; padding: 5px; min-width: 150px;">
          <h4 style="margin: 0 0 8px 0; border-bottom: 1px solid #eee; padding-bottom: 4px; font-size: 14px; color: #333;">
            ${asset.name}
          </h4>
          <p style="margin: 4px 0; font-size: 12px; color: #555;"><b>📍 Div:</b> ${
            asset.division
          }</p>
          <p style="margin: 4px 0; font-size: 12px; color: #555;"><b>🏢 Sub-Div:</b> ${
            asset.subDivision
          }</p>
          <p style="margin: 4px 0; font-size: 12px;">
            <b>🚦 Status:</b> 
            <span style="color:${
              statusColors[asset.status]
            }; font-weight: bold;">${asset.status}</span>
          </p>
        </div>
      `;

      const popup = new maplibregl.Popup({
        offset: 15,
        closeButton: true,
        closeOnClick: true,
        maxWidth: "250px",
      }).setHTML(popupContent);

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([asset.lng, asset.lat])
        .setPopup(popup)
        .addTo(mapRef.current);

      el.addEventListener("click", () => {
        mapRef.current.flyTo({
          center: [asset.lng, asset.lat],
          zoom: 14,
          speed: 1.2,
          essential: true,
        });
      });

      markersRef.current[asset.id] = marker;
    });

    if (filteredData.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      filteredData.forEach((d) => bounds.extend([d.lng, d.lat]));
      mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 13 });
    }
  }, [
    statusFilter,
    divisionFilter,
    subDivisionFilter,
    DUMMY_ASSETS,
    statusColors,
  ]);

  return (
    <div
      style={{
        position: "relative",
        height: mapHeight,
        borderRadius: "10px",
        overflow: "hidden",
      }}
      className={`border ${borderThem}`}
    >
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

export default AssetReportMap;
